import { useContext, useEffect, useState, useCallback } from "react";
import { useLocation, Link } from "react-router-dom";
import { AppContext } from "@/App";
import api, { loginWithGoogle } from "@/lib/api";
import { toast } from "sonner";
import Header from "@/components/Header";
import { Loader2, CheckCircle2, XCircle, Euro, CalendarClock, Flag, ListChecks, User, Sparkles } from "lucide-react";

export default function Admin() {
  const { user, setUser } = useContext(AppContext);
  const location = useLocation();
  const [state, setState] = useState(location.state?.user ? "ok" : null);
  const [bookings, setBookings] = useState([]);
  const [stats, setStats] = useState(null);
  const [busy, setBusy] = useState(null);
  const [drivers, setDrivers] = useState({});
  const [savingDriver, setSavingDriver] = useState(null);
  const [pricing, setPricing] = useState(null);
  const [reviews, setReviews] = useState([]);
  const [savingPricing, setSavingPricing] = useState(false);
  const [draftMsg, setDraftMsg] = useState("");
  const [draftOut, setDraftOut] = useState("");
  const [draftLoading, setDraftLoading] = useState(false);
  const [summaryOut, setSummaryOut] = useState("");
  const [summaryLoading, setSummaryLoading] = useState(false);

  const genDraft = async () => {
    setDraftLoading(true);
    try {
      const res = await api.post("/admin/draft-reply", { message: draftMsg });
      setDraftOut(res.data.draft);
    } catch {
      toast.error("Errore AI");
    } finally {
      setDraftLoading(false);
    }
  };

  const genSummary = async (period) => {
    setSummaryLoading(true);
    setSummaryOut("");
    try {
      const res = await api.get(`/admin/ai-summary?period=${period}`);
      setSummaryOut(res.data.summary);
    } catch {
      toast.error("Errore AI");
    } finally {
      setSummaryLoading(false);
    }
  };

  const savePricing = async () => {
    setSavingPricing(true);
    try {
      await api.put("/admin/pricing", pricing);
      toast.success("Tariffe salvate — attive subito sui preventivi");
    } catch {
      toast.error("Errore nel salvataggio");
    } finally {
      setSavingPricing(false);
    }
  };

  const removeReview = async (id) => {
    try {
      await api.delete(`/admin/reviews/${id}`);
      await load();
    } catch { /* ignore */ }
  };

  const setRate = (i, val) => {
    const p = { ...pricing, flat_routes: [...pricing.flat_routes] };
    p.flat_routes[i] = { ...p.flat_routes[i], price: Number(eVal(val)) };
    setPricing(p);
  };
  const eVal = (v) => (v === "" ? 0 : v);

  const assignDriver = async (id) => {
    setSavingDriver(id);
    try {
      await api.patch(`/admin/bookings/${id}`, drivers[id] || {});
      await load();
    } catch { /* ignore */ } finally {
      setSavingDriver(null);
    }
  };

  const driverVal = (b, field) => drivers[b.booking_id]?.[field] ?? b[field] ?? "";
  const setDriverVal = (b, field, val) =>
    setDrivers({
      ...drivers,
      [b.booking_id]: {
        driver_name: field === "driver_name" ? val : driverVal(b, "driver_name"),
        driver_phone: field === "driver_phone" ? val : driverVal(b, "driver_phone"),
      },
    });

  useEffect(() => {
    if (location.state?.user) {
      setUser(location.state.user);
      return;
    }
    (async () => {
      try {
        const res = await api.get("/auth/me");
        setUser(res.data);
        setState("ok");
      } catch {
        setState("denied");
      }
    })();
  }, [location.state, setUser]);

  const load = useCallback(async () => {
    try {
      const [b, s, p, r] = await Promise.all([
        api.get("/admin/bookings"),
        api.get("/admin/stats"),
        api.get("/admin/pricing"),
        api.get("/admin/reviews"),
      ]);
      setBookings(b.data);
      setStats(s.data);
      setPricing(p.data);
      setReviews(r.data);
    } catch {
      setState("denied");
    }
  }, []);

  useEffect(() => {
    if (state === "ok") load();
  }, [state, load]);

  const mark = async (id, status) => {
    setBusy(id);
    try {
      await api.patch(`/admin/bookings/${id}`, { status });
      await load();
    } catch { /* reload anyway */ } finally {
      setBusy(null);
    }
  };

  const payLabel = (b) => {
    if (b.payment_status === "paid") return { text: "Pagato online", cls: "text-emerald-400" };
    if (b.payment_method === "onboard") return { text: "A bordo", cls: "text-amber-400" };
    return { text: "In attesa", cls: "text-neutral-500" };
  };

  const statusLabel = (s) => ({
    confirmed: "Confermata",
    completed: "Completata",
    cancelled: "Annullata",
    pending_payment: "Da pagare",
  }[s] || s);

  return (
    <div className="min-h-screen bg-[#0B0C0E]">
      <Header />
      <main className="mx-auto max-w-6xl px-4 pb-24 pt-32 sm:px-6" data-testid="admin-page">
        {state === null && (
          <div className="flex justify-center py-20">
            <Loader2 className="h-8 w-8 animate-spin text-[#D4AF37]" />
          </div>
        )}

        {state === "denied" && (
          <div className="card-surface mx-auto max-w-md p-10 text-center" data-testid="admin-denied">
            <h1 className="font-display text-3xl font-semibold text-white">Gestione corse</h1>
            <p className="mt-3 text-sm text-neutral-400">
              Area riservata. Accedi con l'account Google aziendale (R.p.companyservice@gmail.com).
            </p>
            <button data-testid="admin-login-btn" onClick={() => loginWithGoogle("/admin")} className="btn-gold mt-8 w-full">
              Accedi con Google
            </button>
          </div>
        )}

        {state === "ok" && (
          <>
            <p className="label-accent">Area riservata</p>
            <h1 className="font-display mt-2 text-4xl font-medium text-white">Gestione corse</h1>

            {stats && (
              <div className="mt-10 grid grid-cols-2 gap-4 lg:grid-cols-4" data-testid="admin-stats">
                <div className="card-surface p-5">
                  <Euro className="mb-3 h-5 w-5 text-[#D4AF37]" />
                  <p className="font-display text-3xl font-semibold text-[#E5C158]" data-testid="stat-paid-total">€{stats.paid_total.toFixed(2)}</p>
                  <p className="mt-1 text-xs text-neutral-500">Incassato online</p>
                </div>
                <div className="card-surface p-5">
                  <Euro className="mb-3 h-5 w-5 text-amber-400" />
                  <p className="font-display text-3xl font-semibold text-white" data-testid="stat-onboard-total">€{stats.onboard_total.toFixed(2)}</p>
                  <p className="mt-1 text-xs text-neutral-500">Da incassare a bordo</p>
                </div>
                <div className="card-surface p-5">
                  <CalendarClock className="mb-3 h-5 w-5 text-[#D4AF37]" />
                  <p className="font-display text-3xl font-semibold text-white" data-testid="stat-upcoming">{stats.upcoming}</p>
                  <p className="mt-1 text-xs text-neutral-500">Corse in arrivo</p>
                </div>
                <div className="card-surface p-5">
                  <Flag className="mb-3 h-5 w-5 text-emerald-400" />
                  <p className="font-display text-3xl font-semibold text-white" data-testid="stat-completed">{stats.completed}</p>
                  <p className="mt-1 text-xs text-neutral-500">Completate</p>
                </div>
              </div>
            )}

            <h2 className="label-accent mt-14 flex items-center gap-2">
              <ListChecks className="h-4 w-4" /> Tutte le corse ({bookings.length})
            </h2>
            <div className="mt-6 space-y-3">
              {bookings.length === 0 && <p className="text-sm text-neutral-500">Nessuna corsa ancora.</p>}
              {bookings.map((b) => {
                const pay = payLabel(b);
                return (
                  <div key={b.booking_id} data-testid={`admin-booking-${b.booking_id}`} className="card-surface p-5">
                    <div className="flex flex-wrap items-start justify-between gap-4">
                      <div>
                        <p className="font-display text-lg font-semibold text-white">{b.pickup} → {b.dropoff}</p>
                        <p className="mt-1 text-xs text-neutral-500">
                          {b.date} · {b.time} · {b.passengers} pax · {b.vehicle}
                        </p>
                        <p className="mt-1 text-xs text-neutral-400">
                          {b.name} · <a href={`tel:${b.phone}`} className="text-[#E5C158]">{b.phone}</a> · {b.email}
                        </p>
                      </div>
                      <div className="text-right">
                        <p className="font-display text-xl font-semibold text-[#E5C158]">€{b.amount.toFixed(2)}</p>
                        <p className={`font-code text-[10px] uppercase tracking-widest ${pay.cls}`}>{pay.text}</p>
                        <p className="mt-1 font-code text-[10px] uppercase tracking-widest text-neutral-500">{statusLabel(b.status)}</p>
                      </div>
                    </div>
                    <div className="mt-4 flex flex-wrap items-center gap-2 border-t border-[rgba(212,175,55,0.15)] pt-4">
                      <User className="h-4 w-4 text-[#D4AF37]" />
                      <input
                        data-testid={`driver-name-${b.booking_id}`}
                        className="input-dark !w-44 !px-3 !py-1.5 !text-xs"
                        placeholder="Nome autista"
                        value={driverVal(b, "driver_name")}
                        onChange={(e) => setDriverVal(b, "driver_name", e.target.value)}
                      />
                      <input
                        data-testid={`driver-phone-${b.booking_id}`}
                        className="input-dark !w-40 !px-3 !py-1.5 !text-xs"
                        placeholder="Telefono autista"
                        value={driverVal(b, "driver_phone")}
                        onChange={(e) => setDriverVal(b, "driver_phone", e.target.value)}
                      />
                      <button
                        data-testid={`assign-driver-${b.booking_id}`}
                        onClick={() => assignDriver(b.booking_id)}
                        disabled={savingDriver === b.booking_id}
                        className="rounded-full border border-[rgba(212,175,55,0.4)] px-4 py-1.5 text-xs font-semibold text-[#E5C158] transition-colors hover:bg-[rgba(212,175,55,0.08)]"
                      >
                        {savingDriver === b.booking_id ? "…" : b.driver_name ? "Aggiorna autista" : "Assegna autista"}
                      </button>
                      {b.driver_name && (
                        <span className="text-xs text-emerald-400">
                          {b.driver_name}{b.driver_phone ? ` · ${b.driver_phone}` : ""}
                        </span>
                      )}
                    </div>
                    {(b.status === "confirmed" || b.status === "pending_payment") && (
                      <div className="mt-4 flex gap-2 border-t border-[rgba(212,175,55,0.15)] pt-4">
                        <button
                          data-testid={`complete-${b.booking_id}`}
                          onClick={() => mark(b.booking_id, "completed")}
                          disabled={busy === b.booking_id}
                          className="inline-flex items-center gap-1.5 rounded-full bg-[#D4AF37] px-4 py-1.5 text-xs font-semibold text-[#0B0C0E] transition-colors hover:bg-[#F3E08F]"
                        >
                          <CheckCircle2 className="h-3.5 w-3.5" /> Segna completata
                        </button>
                        <button
                          data-testid={`cancel-${b.booking_id}`}
                          onClick={() => mark(b.booking_id, "cancelled")}
                          disabled={busy === b.booking_id}
                          className="inline-flex items-center gap-1.5 rounded-full border border-red-900/60 px-4 py-1.5 text-xs font-semibold text-red-400 transition-colors hover:bg-red-950/40"
                        >
                          <XCircle className="h-3.5 w-3.5" /> Annulla
                        </button>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
            {pricing && (
              <div className="mt-16" data-testid="pricing-editor">
                <h2 className="label-accent">Tariffe — modifiche attive subito</h2>
                <div className="card-surface mt-6 grid gap-4 p-6 sm:grid-cols-2 lg:grid-cols-3">
                  {pricing.flat_routes.map((r, i) => (
                    <div key={r.label}>
                      <label className="label-accent mb-1.5 block !text-[10px]">{r.label}</label>
                      <input
                        data-testid={`rate-${i}`}
                        type="number" step="0.5" min="0"
                        className="input-dark"
                        value={r.price}
                        onChange={(e) => setRate(i, e.target.value)}
                      />
                    </div>
                  ))}
                  <div>
                    <label className="label-accent mb-1.5 block !text-[10px]">Trasferimento urbano</label>
                    <input data-testid="rate-city" type="number" step="0.5" min="0" className="input-dark"
                      value={pricing.city_flat}
                      onChange={(e) => setPricing({ ...pricing, city_flat: Number(e.target.value || 0) })} />
                  </div>
                  <div>
                    <label className="label-accent mb-1.5 block !text-[10px]">€ per km (tratte su misura)</label>
                    <input data-testid="rate-per-km" type="number" step="0.1" min="0" className="input-dark"
                      value={pricing.km_per_km}
                      onChange={(e) => setPricing({ ...pricing, km_per_km: Number(e.target.value || 0) })} />
                  </div>
                  <div>
                    <label className="label-accent mb-1.5 block !text-[10px]">Base tratta su misura (€)</label>
                    <input data-testid="rate-km-base" type="number" step="0.5" min="0" className="input-dark"
                      value={pricing.km_base}
                      onChange={(e) => setPricing({ ...pricing, km_base: Number(e.target.value || 0) })} />
                  </div>
                  <div>
                    <label className="label-accent mb-1.5 block !text-[10px]">Supplemento notturno % (22:00–6:00)</label>
                    <input data-testid="rate-night" type="number" step="1" min="0" className="input-dark"
                      value={pricing.night_pct}
                      onChange={(e) => setPricing({ ...pricing, night_pct: Number(e.target.value || 0) })} />
                  </div>
                  <div>
                    <label className="label-accent mb-1.5 block !text-[10px]">Supplemento festivo % (dom + festivi)</label>
                    <input data-testid="rate-holiday" type="number" step="1" min="0" className="input-dark"
                      value={pricing.holiday_pct}
                      onChange={(e) => setPricing({ ...pricing, holiday_pct: Number(e.target.value || 0) })} />
                  </div>
                  {[["eclass", "Classe E ×"], ["sclass", "Classe S ×"], ["vclass", "Classe V ×"]].map(([k, label]) => (
                    <div key={k}>
                      <label className="label-accent mb-1.5 block !text-[10px]">{label}</label>
                      <input data-testid={`rate-mult-${k}`} type="number" step="0.05" min="0" className="input-dark"
                        value={pricing.multipliers[k]}
                        onChange={(e) => setPricing({ ...pricing, multipliers: { ...pricing.multipliers, [k]: Number(e.target.value || 1) } })} />
                    </div>
                  ))}
                </div>
                <button data-testid="save-pricing-btn" onClick={savePricing} disabled={savingPricing} className="btn-gold mt-4">
                  {savingPricing ? "…" : "Salva tariffe"}
                </button>
              </div>
            )}

            <div className="mt-16" data-testid="reviews-admin">
              <h2 className="label-accent">Recensioni clienti ({reviews.length})</h2>
              <div className="mt-6 space-y-3">
                {reviews.length === 0 && <p className="text-sm text-neutral-500">Nessuna recensione ancora.</p>}
                {reviews.map((r) => (
                  <div key={r.review_id} data-testid={`admin-review-${r.review_id}`} className="card-surface flex items-start justify-between gap-4 p-4">
                    <div>
                      <p className="text-sm text-[#E5C158]">{"★".repeat(r.rating)}<span className="text-neutral-700">{"★".repeat(5 - r.rating)}</span>
                        <span className="ml-2 text-xs text-neutral-500">{r.name} · {r.route_label}</span>
                      </p>
                      <p className="mt-1 text-sm text-neutral-300">“{r.text}”</p>
                    </div>
                    <button
                      data-testid={`delete-review-${r.review_id}`}
                      onClick={() => removeReview(r.review_id)}
                      className="shrink-0 rounded-full border border-red-900/60 px-3 py-1 text-xs text-red-400 transition-colors hover:bg-red-950/40"
                    >
                      Elimina
                    </button>
                  </div>
                ))}
              </div>
            </div>

            <div className="mt-16 grid gap-5 lg:grid-cols-2">
              <div className="card-surface p-6" data-testid="ai-draft-card">
                <h2 className="label-accent flex items-center gap-2"><Sparkles className="h-4 w-4" /> Bozza risposta AI</h2>
                <p className="mt-2 text-xs text-neutral-500">Incolla il messaggio del cliente (WhatsApp o email): l'AI scrive la bozza pronta da copiare.</p>
                <textarea
                  data-testid="draft-input"
                  className="input-dark mt-4 min-h-[100px] resize-none"
                  value={draftMsg}
                  onChange={(e) => setDraftMsg(e.target.value)}
                  placeholder="Messaggio del cliente…"
                />
                <button data-testid="draft-generate-btn" onClick={genDraft} disabled={draftLoading || !draftMsg} className="btn-gold mt-3 !py-2 !text-xs">
                  {draftLoading ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : null}
                  Genera bozza
                </button>
                {draftOut && (
                  <div className="mt-4 rounded-lg bg-[#0B0C0E] p-4">
                    <p className="whitespace-pre-wrap text-sm text-neutral-200" data-testid="draft-output">{draftOut}</p>
                    <button
                      data-testid="draft-copy-btn"
                      onClick={() => { navigator.clipboard.writeText(draftOut); toast.success("Bozza copiata"); }}
                      className="btn-outline-gold mt-3 !px-4 !py-1.5 !text-xs"
                    >
                      Copia
                    </button>
                  </div>
                )}
              </div>
              <div className="card-surface p-6" data-testid="ai-summary-card">
                <h2 className="label-accent flex items-center gap-2"><Sparkles className="h-4 w-4" /> Riepilogo AI</h2>
                <p className="mt-2 text-xs text-neutral-500">Corse, incassi e note operative — scritto dall'AI sui dati veri della dashboard.</p>
                <div className="mt-4 flex gap-2">
                  <button data-testid="summary-day-btn" onClick={() => genSummary("day")} className="btn-outline-gold !px-4 !py-2 !text-xs">Oggi</button>
                  <button data-testid="summary-week-btn" onClick={() => genSummary("week")} className="btn-outline-gold !px-4 !py-2 !text-xs">Settimana</button>
                </div>
                {summaryLoading && <Loader2 className="mt-4 h-5 w-5 animate-spin text-[#D4AF37]" />}
                {summaryOut && <p className="mt-4 whitespace-pre-wrap text-sm leading-relaxed text-neutral-200" data-testid="summary-output">{summaryOut}</p>}
              </div>
            </div>

            <Link to="/" className="btn-outline-gold mt-10">Torna al sito</Link>
          </>
        )}
      </main>
    </div>
  );
}
